
  import VueCropper from "vue-cropperjs"
  import "cropperjs/dist/cropper.css"

  export default {
    name: `ImageCropper`,
    components: {
      VueCropper
    },
    props: {
      withMrz: {
        type: Boolean,
        default: true
      },
      passed: {
        type: Boolean,
        default: false
      },
      documentType: {
        type: String,
        default: `passport`
      },
      document: {
        type: String,
        default: null
      },
      crop: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        rotateValue: 0,
        zoomValue: 1,
        minZoom: 1,
        maxZoom: 1,
        cropperReady: false
      }
    },
    watch: {
      passed(newVal) {
        console.log("ImageCropper passed changed to:", newVal)
      },
      filled(newVal) {
        this.$emit(`update:filled`, newVal)
      },
      // Watch docment type to adjust aspect ratio if needed
      documentType(newVal) {
        this.$nextTick(() => {
          if (this.cropperReady) {
            this.onCropperReady()
          }
        })
      }
    },
    computed: {
      imageOneSrc() {
        return this.document
      },
      filled() {
        return !!this.imageOneSrc
      },
      isMobile() {
        return this.$vuetify.breakpoint.smAndDown
      },
      dummyMrz() {
        if (this.documentType === `passport`) {
          return [`PMCHESCHWEIZER<SAMPLE<<HELVETIA<<<<<<<<<<<<<`, `S0A00K99<6CHE9508015F3209251<<<<<<<<<<<<<<02`].join(
            `\n`
          )
        }

        return [
          `IDCHES0004156<2<<<<<<<<<<<<<<<`,
          `8210058F1510313CHE<<<<<<<<<<<4`,
          `MUSTERFRAU<<ALEXANDRA<<<<<<<<<`
        ].join(`\n`)
      }
    },
    methods: {
      clickZoom(delta) {
        this.zoomValue = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoomValue + delta))
        this.onZoom(this.zoomValue)
      },
      onRotate() {
        this.onRealTime()
        const cropper = this.$refs.cropper.cropper
        cropper.rotate(this.rotateValue - cropper.getData().rotate)
      },
      setCrop() {
        if (this.crop) {
          const cropper = this.$refs?.cropper?.cropper
          if (!cropper) return

          // Apply rotation first
          if (this.crop.rotate !== undefined) {
            this.rotateValue = this.crop.rotate
            cropper.rotateTo(this.crop.rotate)
          }

          // Apply zoom if available
          if (this.crop.zoom !== undefined) {
            this.zoomValue = this.crop.zoom
            cropper.zoomTo(this.crop.zoom)
          }

          // Position the canvas (image) to align with the saved crop data
          // The crop box stays fixed/centered, we move the image underneath it
          if (
            this.crop.x !== undefined &&
            this.crop.y !== undefined &&
            this.crop.width !== undefined &&
            this.crop.height !== undefined
          ) {
            const cropBoxData = cropper.getCropBoxData()
            const imageData = cropper.getImageData()

            // The saved crop coordinates are in original image space
            // imageData gives us the ratio between displayed canvas and natural image
            const ratio = imageData.width / imageData.naturalWidth

            // Calculate where the top-left of the saved crop area is on the displayed canvas
            const cropLeftOnCanvas = this.crop.x * ratio
            const cropTopOnCanvas = this.crop.y * ratio

            // We want this point to align with the top-left of the crop box
            // So the canvas needs to be offset by: cropBox.position - crop.position
            const newLeft = cropBoxData.left - cropLeftOnCanvas
            const newTop = cropBoxData.top - cropTopOnCanvas

            const canvasData = cropper.getCanvasData()
            cropper.setCanvasData({
              left: newLeft,
              top: newTop,
              width: canvasData.width,
              height: canvasData.height
            })
          }
        }
      },
      onCropperReady() {
        this.cropperReady = true
        const cropper = this.$refs?.cropper?.cropper
        if (!cropper) {
          return
        }
        const isPassport = this.documentType === `passport`

        if (isPassport) {
          cropper.setAspectRatio(0.71) // 0.71 is the aspect ratio for passports
          const { width: cw, height: ch } = cropper.getContainerData()
          const boxHeight = ch * 0.9
          const boxWidth = boxHeight * cropper.options.aspectRatio
          const left = (cw - boxWidth) / 2
          const top = (ch - boxHeight) / 2
          cropper.setCropBoxData({ left, top, width: boxWidth, height: boxHeight })
        } else {
          cropper.setAspectRatio(1.592) // 1.592 is the aspect ratio for ID cards
          const { width: cw, height: ch } = cropper.getContainerData()
          const boxHeight = ch * 0.7
          const boxWidth = boxHeight * cropper.options.aspectRatio
          const left = (cw - boxWidth) / 2
          const top = (ch - boxHeight) / 2
          cropper.setCropBoxData({ left, top, width: boxWidth, height: boxHeight })
        }

        this.computeZoomLimits()

        this.zoomValue = this.minZoom * 2
        this.onZoom(this.zoomValue)

        this.rotateValue = cropper.getData().rotate || 0
        this.setCrop()
        console.log("Crop applied:", this.crop)
      },
      onRealTime() {
        this.$emit(`image-changed`)
      },
      computeZoomLimits() {
        const cropper = this.$refs.cropper.cropper
        const imgData = cropper.getImageData()
        const cropBoxData = cropper.getCropBoxData()

        // minimum zoom so that the image at least covers the crop‐box in both dimensions:
        const minCropBox = Math.min(cropBoxData.width, cropBoxData.height)
        const maxImgDim = Math.max(imgData.naturalWidth, imgData.naturalHeight)
        const minZWithoutPadding = minCropBox / maxImgDim
        const minZ = minZWithoutPadding * 0.9 // add some padding to avoid zooming too close

        // maximum zoom: you might cap at 1 (natural size), or higher if you want to allow upscaling
        const maxZ = minZ * 5

        this.minZoom = minZ
        this.maxZoom = maxZ
        this.zoomValue = minZ
        // immediately apply the new zoom
        cropper.zoomTo(this.zoomValue)
      },
      clear() {
        this.$emit("cleared")
      },
      onZoom(value) {
        this.onRealTime()
        // zoomTo expects a ratio relative to natural size:
        this.$refs.cropper.zoomTo(value)
      },
      onWheelZoom(e) {
        this.onRealTime()

        const step = (this.maxZoom - this.minZoom) / 50

        const delta = -(e.deltaY / 100) * step
        let next = this.zoomValue + delta
        // clamp
        next = Math.min(this.maxZoom, Math.max(this.minZoom, next))

        this.zoomValue = next
        this.$refs.cropper.zoomTo(next)
      },
      rotateImage(value) {
        this.rotateValue = ((this.rotateValue + 180 + 360 + value) % 360) - 180
        this.onRotate()
      },
      cropImage() {
        const MAX_W = 2400
        const MAX_H = 1500

        // get raw crop-box size
        const { width: cropW, height: cropH } = this.$refs.cropper.getData()
        let outW = cropW
        let outH = cropH
        const ratio = cropW / cropH

        // scale down to fit in MAX_W × MAX_H
        if (outW > MAX_W) {
          outW = MAX_W
          outH = outW / ratio
        }
        if (outH > MAX_H) {
          outH = MAX_H
          outW = outH * ratio
        }

        const canvas = this.$refs.cropper.getCroppedCanvas({
          width: Math.round(outW),
          height: Math.round(outH),
          imageSmoothingQuality: `high`
        })
        const dataUrl = canvas.toDataURL(`image/jpeg`, 0.9)
        return dataUrl
      },
      getFullImage() {
        return this.document
      },
      getCropperData() {
        const cropper = this.$refs.cropper?.cropper
        if (!cropper) return null

        const data = cropper.getData()
        return {
          x: data.x,
          y: data.y,
          width: data.width,
          height: data.height,
          rotate: data.rotate,
          zoom: this.zoomValue
        }
      }
    }
  }
